import { useState, type ReactNode } from 'react';
import { useApi } from '../lib/api';
import { useList } from '../lib/hooks';
import { qs } from '../lib/util';
import type { CuratedEntity, ListResp, Numeric } from '../lib/types';
import {
  Banner,
  ConfBar,
  DataBlock,
  Empty,
  ErrorBox,
  Field,
  Loading,
  Modal,
  Pager,
  StatusChip,
  Table,
  Toolbar,
  cx,
} from '../ui';

type Row = Record<string, unknown>;
const LIMIT = 50;

const S = (v: unknown): string => (v == null || v === '' ? '—' : String(v));
const M = (v: unknown): ReactNode => <span className="mono">{S(v)}</span>;

interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select';
  options?: [string, string][];
  required?: boolean;
}
interface ColDef {
  h: string;
  cell: (r: Row) => ReactNode;
}
interface EntityCfg {
  title: string;
  banner: ReactNode;
  base: string; // list / create / PUT:id / DELETE:id share this
  cols: ColDef[];
  filters: FieldDef[];
  createFields: FieldDef[];
  editFields: FieldDef[];
  flagged?: boolean;
}

const RELATION: [string, string][] = [
  ['interchange', 'interchange'],
  ['equivalent', 'equivalent'],
  ['supersedes', 'supersedes'],
];
const STATUS: [string, string][] = [
  ['confirmed', 'confirmed'],
  ['suspected', 'suspected'],
];

const CONFIG: Record<CuratedEntity, EntityCfg> = {
  anticross: {
    title: 'Anticross OE → part',
    banner: (
      <>
        <b>Anticross · OE → part</b> — "this part does NOT fit this OE". Speaks only when somebody asks about{' '}
        <b>that OE</b>: the part is flagged in the answer, never hidden. For "these two products are not
        interchangeable" use <b>Anticross part ↔ part</b>.
      </>
    ),
    base: '/v1/admin/anticross',
    flagged: true,
    cols: [
      { h: 'OE', cell: (r) => M(r.oeNorm) },
      { h: 'Brand', cell: (r) => S(r.brandNorm) },
      { h: 'Article', cell: (r) => M(r.articleNorm) },
      { h: 'Status', cell: (r) => <StatusChip status={r.status as 'confirmed' | 'suspected'} /> },
      { h: 'Confidence', cell: (r) => <ConfBar value={r.confidence as Numeric} /> },
      { h: 'Evidence', cell: (r) => <span className="num">{S(r.evidenceCount)}</span> },
      { h: 'Reason', cell: (r) => <span className="text-muted">{S(r.reason)}</span> },
    ],
    filters: [
      { key: 'oe', label: 'OE', type: 'text' },
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'article', label: 'Article', type: 'text' },
      { key: 'status', label: 'Any status', type: 'select', options: [['', 'Any status'], ...STATUS] },
    ],
    createFields: [
      { key: 'oe', label: 'OE', type: 'text', required: true },
      { key: 'brand', label: 'Brand', type: 'text', required: true },
      { key: 'article', label: 'Article', type: 'text', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'evidenceCount', label: 'Evidence count', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
    editFields: [
      { key: 'status', label: 'Status', type: 'select', options: STATUS },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'evidenceCount', label: 'Evidence count', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
  },
  'anticross-part-part': {
    title: 'Anticross part ↔ part',
    banner: (
      <>
        <b>Anticross · part ↔ part</b> — "these two products are NOT interchangeable". Speaks when somebody asks about{' '}
        <b>either</b> product (brand + code): the other one is flagged in the answer, never hidden.
      </>
    ),
    base: '/v1/admin/anticross/part-part',
    flagged: true,
    cols: [
      { h: 'Part A', cell: (r) => M(`${S(r.aBrandNorm)} ${S(r.aArticleNorm)}`) },
      { h: '', cell: () => <span className="text-muted">↮</span> },
      { h: 'Part B', cell: (r) => M(`${S(r.bBrandNorm)} ${S(r.bArticleNorm)}`) },
      { h: 'Status', cell: (r) => <StatusChip status={r.status as 'confirmed' | 'suspected'} /> },
      { h: 'Confidence', cell: (r) => <ConfBar value={r.confidence as Numeric} /> },
      { h: 'Reason', cell: (r) => <span className="text-muted">{S(r.reason)}</span> },
    ],
    filters: [
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'article', label: 'Article', type: 'text' },
      { key: 'status', label: 'Any status', type: 'select', options: [['', 'Any status'], ...STATUS] },
    ],
    createFields: [
      { key: 'aBrand', label: 'A brand', type: 'text', required: true },
      { key: 'aArticle', label: 'A article', type: 'text', required: true },
      { key: 'bBrand', label: 'B brand', type: 'text', required: true },
      { key: 'bArticle', label: 'B article', type: 'text', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
    editFields: [
      { key: 'status', label: 'Status', type: 'select', options: STATUS },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
  },
  'xref-oe-part': {
    title: 'Cross OE → part',
    banner: (
      <>
        <b>Cross · OE → part</b> — parts we assert for an OE beyond what the catalogues return. Speaks only when
        somebody asks about <b>that OE</b>: the part is added to the answer, first, marked{' '}
        <span className="mono">_cati.curated</span>.
      </>
    ),
    base: '/v1/admin/xref/oe-part',
    cols: [
      { h: 'OE', cell: (r) => M(r.oeNorm) },
      { h: 'Brand', cell: (r) => S(r.brandNorm) },
      { h: 'Article', cell: (r) => M(r.articleNorm) },
      { h: 'Name', cell: (r) => <span className="text-muted">{S(r.name)}</span> },
      { h: 'Confidence', cell: (r) => <ConfBar value={r.confidence as Numeric} /> },
      { h: 'Reason', cell: (r) => <span className="text-muted">{S(r.reason)}</span> },
    ],
    filters: [
      { key: 'oe', label: 'OE', type: 'text' },
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'article', label: 'Article', type: 'text' },
    ],
    createFields: [
      { key: 'oe', label: 'OE', type: 'text', required: true },
      { key: 'brand', label: 'Brand', type: 'text', required: true },
      { key: 'article', label: 'Article', type: 'text', required: true },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
    editFields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
  },
  'xref-part-part': {
    title: 'Cross part ↔ part',
    banner: (
      <>
        <b>Cross · part ↔ part</b> — "these two products are interchangeable". Speaks when somebody asks about{' '}
        <b>either</b> product (brand + code): the other one is added to the answer, first, marked{' '}
        <span className="mono">_cati.curated</span>.
      </>
    ),
    base: '/v1/admin/xref/part-part',
    cols: [
      { h: 'Part A', cell: (r) => M(`${S(r.aBrandNorm)} ${S(r.aArticleNorm)}`) },
      { h: '', cell: () => <span className="text-muted">↔</span> },
      { h: 'Part B', cell: (r) => M(`${S(r.bBrandNorm)} ${S(r.bArticleNorm)}`) },
      { h: 'Relation', cell: (r) => <span className="chip">{S(r.relation)}</span> },
      { h: 'Confidence', cell: (r) => <ConfBar value={r.confidence as Numeric} /> },
      { h: 'Reason', cell: (r) => <span className="text-muted">{S(r.reason)}</span> },
    ],
    filters: [
      { key: 'aBrand', label: 'A brand', type: 'text' },
      { key: 'aArticle', label: 'A article', type: 'text' },
      { key: 'bBrand', label: 'B brand', type: 'text' },
      { key: 'bArticle', label: 'B article', type: 'text' },
    ],
    createFields: [
      { key: 'aBrand', label: 'A brand', type: 'text', required: true },
      { key: 'aArticle', label: 'A article', type: 'text', required: true },
      { key: 'bBrand', label: 'B brand', type: 'text', required: true },
      { key: 'bArticle', label: 'B article', type: 'text', required: true },
      { key: 'relation', label: 'Relation', type: 'select', options: RELATION },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
    editFields: [
      { key: 'relation', label: 'Relation', type: 'select', options: RELATION },
      { key: 'confidence', label: 'Confidence (0–1)', type: 'number' },
      { key: 'reason', label: 'Reason', type: 'text' },
    ],
  },
};

function buildBody(fields: FieldDef[], vals: Record<string, string>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const f of fields) {
    const v = vals[f.key];
    if (v == null || v === '') continue;
    body[f.key] = f.type === 'number' ? Number(v) : v;
  }
  return body;
}

/** What the server says about a code no catalogue knows (HTTP 409). See src/curated/check.ts. */
interface Suggestion {
  brand: string;
  article: string;
  why: string;
}
interface Warnings {
  oe?: { oe: string; asProduct: Suggestion[] };
  products: { brand: string; article: string; suggestions: Suggestion[] }[];
}
type SubmitResult = null | { error: string } | { warnings: Warnings };

/** The server's normalization, near enough to find which fields a warning is about. */
const norm = (v: string): string => v.toUpperCase().replace(/[^\p{L}\p{N}]/gu, '');
const normArticle = (v: string): string => norm(v).replace(/^0+(?=.)/, '');

/** The (brand, article) field pairs a form has, in order. */
const PAIRS: [string, string][] = [
  ['brand', 'article'],
  ['aBrand', 'aArticle'],
  ['bBrand', 'bArticle'],
];

function errMsg(status: number, data: unknown): string {
  if (data && typeof data === 'object' && 'error' in data) return String((data as { error: unknown }).error);
  return `Request failed (HTTP ${status}).`;
}

function FieldInput({ f, value, onChange }: { f: FieldDef; value: string; onChange: (v: string) => void }) {
  if (f.type === 'select') {
    return (
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {(f.options ?? []).map(([v, label]) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      className={cx('input', f.type === 'text' && 'mono')}
      type={f.type === 'number' ? 'number' : 'text'}
      step={f.type === 'number' ? 'any' : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function EntityForm({
  title,
  fields,
  initial,
  submitLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  fields: FieldDef[];
  initial: Record<string, string>;
  submitLabel: string;
  onSubmit: (vals: Record<string, string>, force: boolean) => Promise<SubmitResult>;
  onClose: () => void;
}) {
  const [vals, setVals] = useState<Record<string, string>>(initial);
  const [err, setErr] = useState('');
  const [warnings, setWarnings] = useState<Warnings | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(force = false) {
    for (const f of fields) if (f.required && !(vals[f.key] ?? '').trim()) return setErr(`${f.label} is required.`);
    setBusy(true);
    setErr('');
    const r = await onSubmit(vals, force);
    setBusy(false);
    if (r && 'error' in r) setErr(r.error);
    if (r && 'warnings' in r) setWarnings(r.warnings);
  }

  // Put the proposed spelling into the fields the warning is about.
  function use(typed: { brand: string; article: string }, s: Suggestion) {
    const pairs = PAIRS.filter(([b, a]) => fields.some((f) => f.key === b) && fields.some((f) => f.key === a));
    const hit =
      pairs.find(([b, a]) => norm(vals[b] ?? '') === typed.brand && normArticle(vals[a] ?? '') === typed.article) ??
      pairs[0];
    if (!hit) return;
    setVals((v) => ({ ...v, [hit[0]]: s.brand, [hit[1]]: s.article }));
    setWarnings(null);
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          {warnings && (
            <button className="btn" onClick={() => submit(true)} disabled={busy}>
              Save anyway
            </button>
          )}
          <button className="btn btn-primary" onClick={() => submit(false)} disabled={busy}>
            {busy ? 'Saving…' : submitLabel}
          </button>
        </>
      }
    >
      {fields.map((f) => (
        <Field key={f.key} label={f.label}>
          <FieldInput f={f} value={vals[f.key] ?? ''} onChange={(v) => setVals((s) => ({ ...s, [f.key]: v }))} />
        </Field>
      ))}
      {warnings && (
        <div className="text-[12px] flex flex-col gap-2">
          {warnings.products.map((w) => (
            <div key={`${w.brand} ${w.article}`}>
              <div className="text-bad">
                <span className="mono">
                  {w.brand} {w.article}
                </span>{' '}
                is not a product the catalogues know — a rule on it will never match.
              </div>
              {w.suggestions.length > 0 ? (
                w.suggestions.map((s) => (
                  <div key={`${s.brand} ${s.article}`} className="flex items-center gap-2 mt-1">
                    <button className="btn btn-sm" onClick={() => use(w, s)}>
                      Use {s.brand} {s.article}
                    </button>
                    <span className="text-muted">{s.why}</span>
                  </div>
                ))
              ) : (
                <div className="text-muted">
                  No similar code found. If the product is real but nobody has searched for it yet, save anyway.
                </div>
              )}
            </div>
          ))}
          {warnings.oe && (
            <div className="text-bad">
              <span className="mono">{warnings.oe.oe}</span> is not an OE any vehicle maker publishes — it is{' '}
              {warnings.oe.asProduct.map((p) => `${p.brand} ${p.article}`).join(', ')}. A rule between two
              products belongs in the <b>part ↔ part</b> table.
            </div>
          )}
        </div>
      )}
      {err && <div className="text-[12px] text-bad">{err}</div>}
    </Modal>
  );
}

export default function Curated({ entity }: { entity: CuratedEntity }) {
  const cfg = CONFIG[entity];
  const { request } = useApi();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);

  const path = `${cfg.base}${qs({ ...filters, limit: LIMIT, offset })}`;
  const { data, loading, error, reload } = useList<Row>(path);

  const setFilter = (key: string, val: string) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setOffset(0);
  };

  async function create(vals: Record<string, string>, force: boolean): Promise<SubmitResult> {
    const r = await request('POST', cfg.base, { ...buildBody(cfg.createFields, vals), ...(force ? { force: true } : {}) });
    if (r.status < 300) {
      setAdding(false);
      reload();
      return null;
    }
    const data = r.data as { warnings?: Warnings } | null;
    if (r.status === 409 && data?.warnings) return { warnings: data.warnings };
    return { error: errMsg(r.status, r.data) };
  }
  async function update(id: number, vals: Record<string, string>): Promise<SubmitResult> {
    const r = await request('PUT', `${cfg.base}/${id}`, buildBody(cfg.editFields, vals));
    if (r.status < 300) {
      setEditing(null);
      reload();
      return null;
    }
    return { error: errMsg(r.status, r.data) };
  }
  async function remove(id: number) {
    if (!window.confirm('Delete this row?')) return;
    await request('DELETE', `${cfg.base}/${id}`);
    reload();
  }

  const items = (data as ListResp<Row> | null)?.items ?? [];
  const editInitial = (row: Row): Record<string, string> =>
    Object.fromEntries(cfg.editFields.map((f) => [f.key, row[f.key] == null ? '' : String(row[f.key])]));

  return (
    <>
      <Banner>{cfg.banner}</Banner>
      <Toolbar>
        {cfg.filters.map((f) =>
          f.type === 'select' ? (
            <select key={f.key} className="input" style={{ width: 'auto' }} value={filters[f.key] ?? ''} onChange={(e) => setFilter(f.key, e.target.value)}>
              {(f.options ?? []).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <input key={f.key} className="input mono" style={{ width: 150 }} placeholder={f.label} value={filters[f.key] ?? ''} onChange={(e) => setFilter(f.key, e.target.value)} />
          ),
        )}
        <button className="btn ml-auto" onClick={() => setAdding(true)}>
          ＋ Add
        </button>
      </Toolbar>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DataBlock
        title={cfg.title}
        sub={data ? `${data.total.toLocaleString()} total` : undefined}
        raw={data}
        right={data ? <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} /> : undefined}
      >
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>No rows. Use ＋ Add or the Import panel.</Empty>
        ) : (
          <Table cols={[...cfg.cols.map((c) => c.h), '']}>
            {items.map((r) => (
              <tr key={String(r.id)} className={cfg.flagged ? 'flagged' : undefined}>
                {cfg.cols.map((c, j) => (
                  <td key={j}>{c.cell(r)}</td>
                ))}
                <td>
                  <div className="flex gap-1.5">
                    <button className="btn btn-sm" onClick={() => setEditing(r)}>
                      Edit
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(Number(r.id))}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>

      {adding && (
        <EntityForm
          title={`Add — ${cfg.title}`}
          fields={cfg.createFields}
          initial={{}}
          submitLabel="Create"
          onSubmit={create}
          onClose={() => setAdding(false)}
        />
      )}
      {editing && (
        <EntityForm
          title={`Edit #${String(editing.id)}`}
          fields={cfg.editFields}
          initial={editInitial(editing)}
          submitLabel="Save"
          onSubmit={(vals) => update(Number(editing.id), vals)}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
